import { useActivityTracker } from "hooks/useActivityTracker";
import { useEffect, useState } from "react";

type Consent = {
  analytics: boolean;
};

type CookieSettingsProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (consent: Consent) => void;
  initialConsent?: Consent | null;
};

const STORAGE_KEY = "cookie-consent";

const CookieSettings: React.FC<CookieSettingsProps> = ({
  isOpen,
  onClose,
  onSave,
  initialConsent,
}) => {
  const [analytics, setAnalytics] = useState(false);

  /* Load saved consent when modal opens */
  useEffect(() => {
    if (!isOpen) return;

    if (initialConsent) {
      setAnalytics(!!initialConsent.analytics);
    } else {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setAnalytics(!!parsed.analytics);
        } catch {
          setAnalytics(false);
        }
      }
    }
  }, [isOpen, initialConsent]);

  /* Prevent background scroll */
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  const { trackActivity } = useActivityTracker();
  const handleSave = (value: Consent) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...value,
        timestamp: Date.now(),
        version: "1.0",
      })
    );

    onSave(value);
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />

      {/* Panel */}
      <aside
        className={`fixed left-0 top-0 z-[102] h-full w-full max-w-sm bg-white shadow-xl
        transform transition-transform duration-300 ease-in-out flex flex-col
        ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
        aria-hidden={!isOpen}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">
            Cookie Settings
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close cookie settings"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <p className="text-sm text-gray-600">
            Manage your cookie preferences below.
          </p>

          {/* Essential */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-900">
                  Essential Cookies
                </h3>
                <p className="text-xs text-gray-500">
                  Required for the website to function properly.
                </p>
              </div>
              <span className="text-xs font-semibold text-green-600">
                Always Active
              </span>
            </div>
          </div>

          {/* Analytics */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-900">
                  Analytics Cookies
                </h3>
                <p className="text-xs text-gray-500">
                  Help us understand site usage.
                </p>
              </div>
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex gap-3 shrink-0">
          <button
            className="flex-1 rounded-md bg-gray-100 px-4 py-2 text-sm font-medium hover:bg-gray-200"
            onClick={() => {
                handleSave({ analytics: false })
                 trackActivity(
                    "ConsentRejected",
                    "Analytics Disabled",
                    "User rejected analytics cookies"
                );

            }}
          >
            Reject All
          </button>

          <button
            className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            onClick={() => handleSave({ analytics })}
          >
            Save Preferences
          </button>
        </div>
      </aside>
    </>
  );
};

export default CookieSettings;